import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Add,
  Delete,
  Edit,
  Save,
  Cancel,
  Security,
  RemoveCircleOutline,
} from '@mui/icons-material';
import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { LoadingState } from '../components/loading-state';
import { formatDateTime, formatRelativeTime } from '../utils/formatting';
import {
  useGetBundleQuery,
  useUpdateBundleMutation,
  useDeleteBundleMutation,
  useGetCapabilitiesQuery,
  useAddBundleGrantMutation,
  useUpdateBundleGrantMutation,
  useRemoveBundleGrantMutation,
} from '../store/hooks';
import type { GrantResponse, CapabilityResponse } from '@store/generated/admin';

export default function BundleDetailPage() {
  const navigate = useNavigate();
  const { bundleId } = useParams<{ bundleId: string }>();
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addGrantDialogOpen, setAddGrantDialogOpen] = useState(false);
  const [removeGrantDialogOpen, setRemoveGrantDialogOpen] = useState(false);
  const [editGrantDialogOpen, setEditGrantDialogOpen] = useState(false);
  const [selectedGrant, setSelectedGrant] = useState<GrantResponse | null>(null);
  const [editGrantForm, setEditGrantForm] = useState({
    metering_type: 'none',
    quota: '',
    usage_period: '',
  });
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    stripe_product_id: '',
  });
  const [newGrant, setNewGrant] = useState({
    capability: null as CapabilityResponse | null,
    metering_type: 'none',
    quota: '',
    usage_period: '',
  });

  const {
    data: bundle,
    isLoading,
    error,
    refetch,
  } = useGetBundleQuery({ bundleId: bundleId! }, { skip: !bundleId });

  const { data: capabilitiesData } = useGetCapabilitiesQuery();

  const [updateBundle, { isLoading: isUpdating }] = useUpdateBundleMutation();
  const [deleteBundle, { isLoading: isDeleting }] = useDeleteBundleMutation();
  const [addGrant, { isLoading: isAddingGrant }] = useAddBundleGrantMutation();
  const [updateGrant, { isLoading: isUpdatingGrant }] = useUpdateBundleGrantMutation();
  const [removeGrant, { isLoading: isRemovingGrant }] = useRemoveBundleGrantMutation();

  const capabilities = capabilitiesData?.capabilities || [];
  const grants = bundle?.grants || [];

  // Filter out capabilities already granted
  const availableCapabilities = capabilities.filter(
    cap => !grants.some(g => g.capability_id === cap.capability_id)
  );

  const handleEditStart = useCallback(() => {
    if (bundle) {
      setEditForm({
        name: bundle.name,
        description: bundle.description || '',
        stripe_product_id: bundle.stripe_product_id,
      });
      setIsEditing(true);
    }
  }, [bundle]);

  const handleEditCancel = useCallback(() => {
    setIsEditing(false);
    setEditForm({ name: '', description: '', stripe_product_id: '' });
  }, []);

  const handleEditSave = useCallback(async () => {
    if (!bundleId || !editForm.name.trim()) return;

    try {
      await updateBundle({
        bundleId,
        updateBundleRequest: {
          name: editForm.name.trim(),
          description: editForm.description.trim() || null,
          stripe_product_id: editForm.stripe_product_id.trim() || null,
        },
      }).unwrap();
      setIsEditing(false);
      refetch();
    } catch (err) {
      console.error('Failed to update bundle:', err);
    }
  }, [bundleId, editForm, updateBundle, refetch]);

  const handleDeleteOpen = useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteClose = useCallback(() => {
    setDeleteDialogOpen(false);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!bundleId) return;

    try {
      await deleteBundle({ bundleId }).unwrap();
      navigate('/admin-v2/bundles');
    } catch (err) {
      console.error('Failed to delete bundle:', err);
    }
  }, [bundleId, deleteBundle, navigate]);

  const handleAddGrantOpen = useCallback(() => {
    setNewGrant({
      capability: null,
      metering_type: 'none',
      quota: '',
      usage_period: '',
    });
    setAddGrantDialogOpen(true);
  }, []);

  const handleAddGrantClose = useCallback(() => {
    setAddGrantDialogOpen(false);
    setNewGrant({
      capability: null,
      metering_type: 'none',
      quota: '',
      usage_period: '',
    });
  }, []);

  const handleAddGrantSubmit = useCallback(async () => {
    if (!bundleId || !newGrant.capability) return;

    try {
      await addGrant({
        bundleId,
        addGrantRequest: {
          capability_id: newGrant.capability.capability_id,
          metering_type: newGrant.metering_type,
          quota: newGrant.quota ? parseInt(newGrant.quota, 10) : null,
          usage_period: newGrant.usage_period || null,
        },
      }).unwrap();
      handleAddGrantClose();
      refetch();
    } catch (err) {
      console.error('Failed to add grant:', err);
    }
  }, [bundleId, newGrant, addGrant, handleAddGrantClose, refetch]);

  const handleRemoveGrantClick = useCallback((grant: GrantResponse) => {
    setSelectedGrant(grant);
    setRemoveGrantDialogOpen(true);
  }, []);

  const handleRemoveGrantClose = useCallback(() => {
    setRemoveGrantDialogOpen(false);
    setSelectedGrant(null);
  }, []);

  const handleRemoveGrantConfirm = useCallback(async () => {
    if (!bundleId || !selectedGrant) return;

    try {
      await removeGrant({
        bundleId,
        capabilityId: selectedGrant.capability_id,
      }).unwrap();
      handleRemoveGrantClose();
      refetch();
    } catch (err) {
      console.error('Failed to remove grant:', err);
    }
  }, [bundleId, selectedGrant, removeGrant, handleRemoveGrantClose, refetch]);

  const handleEditGrantClick = useCallback((grant: GrantResponse) => {
    setSelectedGrant(grant);
    setEditGrantForm({
      metering_type: grant.metering_type || 'none',
      quota: grant.quota !== null ? String(grant.quota) : '',
      usage_period: grant.usage_period || '',
    });
    setEditGrantDialogOpen(true);
  }, []);

  const handleEditGrantClose = useCallback(() => {
    setEditGrantDialogOpen(false);
    setSelectedGrant(null);
    setEditGrantForm({ metering_type: 'none', quota: '', usage_period: '' });
  }, []);

  const handleEditGrantSubmit = useCallback(async () => {
    if (!bundleId || !selectedGrant) return;

    try {
      await updateGrant({
        bundleId,
        capabilityId: selectedGrant.capability_id,
        updateGrantRequest: {
          metering_type: editGrantForm.metering_type,
          quota: editGrantForm.quota ? parseInt(editGrantForm.quota, 10) : null,
          usage_period: editGrantForm.usage_period || null,
        },
      }).unwrap();

      handleEditGrantClose();
      refetch();
    } catch (err) {
      console.error('Failed to update grant:', err);
    }
  }, [bundleId, selectedGrant, editGrantForm, updateGrant, handleEditGrantClose, refetch]);

  if (!bundleId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Invalid Bundle ID</h2>
            <Button onClick={() => navigate('/admin-v2/bundles')} className="mt-4">
              Back to Bundles
            </Button>
          </div>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingState message="Loading bundle..." />
        </main>
      </div>
    );
  }

  if (error || !bundle) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Error loading bundle</h2>
            <p className="text-gray-600 mt-2">Unable to fetch bundle details.</p>
            <Button onClick={() => navigate('/admin-v2/bundles')} className="mt-4">
              Back to Bundles
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2/bundles" />
        </div>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Bundle' : 'Bundle Details'}
          </h1>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={handleEditCancel}
                  disabled={isUpdating}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleEditSave}
                  disabled={!editForm.name.trim() || isUpdating}
                >
                  {isUpdating ? 'Saving...' : 'Save'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outlined" startIcon={<Edit />} onClick={handleEditStart}>
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={handleDeleteOpen}
                >
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {/* Bundle Info Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" className="mb-4">
                Bundle Information
              </Typography>
              {isEditing ? (
                <div className="space-y-4">
                  <TextField
                    label="Name"
                    fullWidth
                    value={editForm.name}
                    onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    required
                    helperText="Display name for this bundle"
                  />
                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    rows={3}
                    value={editForm.description}
                    onChange={e => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                    helperText="Optional description of what this bundle includes"
                  />
                  <TextField
                    label="Stripe Product ID"
                    fullWidth
                    value={editForm.stripe_product_id}
                    onChange={e =>
                      setEditForm(prev => ({ ...prev, stripe_product_id: e.target.value }))
                    }
                    helperText="The Stripe product ID linked to this bundle"
                  />
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <Typography variant="subtitle2" className="text-gray-500">
                      Name
                    </Typography>
                    <Typography variant="h6">{bundle.name}</Typography>
                  </div>

                  <div>
                    <Typography variant="subtitle2" className="text-gray-500">
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {bundle.description || (
                        <span className="text-gray-400 italic">No description</span>
                      )}
                    </Typography>
                  </div>

                  <div>
                    <Typography variant="subtitle2" className="text-gray-500">
                      Stripe Product ID
                    </Typography>
                    <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                      {bundle.stripe_product_id}
                    </code>
                  </div>

                  <div>
                    <Typography variant="subtitle2" className="text-gray-500">
                      Bundle ID
                    </Typography>
                    <Typography variant="body2" className="font-mono text-gray-600">
                      {bundle.bundle_id}
                    </Typography>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <Typography variant="subtitle2" className="text-gray-500">
                        Created
                      </Typography>
                      <Typography variant="body1">
                        {formatDateTime(new Date(bundle.created_at))}
                      </Typography>
                      <Typography variant="body2" className="text-gray-500">
                        {formatRelativeTime(new Date(bundle.created_at))}
                      </Typography>
                    </div>

                    <div>
                      <Typography variant="subtitle2" className="text-gray-500">
                        Last Updated
                      </Typography>
                      <Typography variant="body1">
                        {formatDateTime(new Date(bundle.updated_at))}
                      </Typography>
                      <Typography variant="body2" className="text-gray-500">
                        {formatRelativeTime(new Date(bundle.updated_at))}
                      </Typography>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Capabilities Card */}
          <Card>
            <CardContent>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Typography variant="h6">Granted Capabilities</Typography>
                  <Typography variant="body2" className="text-gray-500">
                    Capabilities included in this bundle
                  </Typography>
                </div>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={handleAddGrantOpen}
                  disabled={availableCapabilities.length === 0}
                >
                  Add Capability
                </Button>
              </div>

              {grants.length === 0 ? (
                <div className="text-center py-8">
                  <Security className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                  <Typography variant="body1" className="text-gray-500">
                    No capabilities granted yet
                  </Typography>
                  <Typography variant="body2" className="text-gray-400">
                    Add capabilities to define what this bundle enables
                  </Typography>
                </div>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Capability</TableCell>
                      <TableCell>Metering</TableCell>
                      <TableCell>Quota</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {grants.map(grant => (
                      <TableRow key={grant.grant_id}>
                        <TableCell>
                          <div className="flex items-center">
                            <Security className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="font-medium">
                              {grant.capability_name || grant.capability_id}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={grant.metering_type}
                            size="small"
                            color={grant.metering_type === 'none' ? 'default' : 'primary'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {grant.quota !== null ? grant.quota : (
                            <span className="text-gray-400">Unlimited</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {grant.usage_period || (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit grant">
                            <IconButton
                              size="small"
                              onClick={() => handleEditGrantClick(grant)}
                              aria-label="Edit grant settings"
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove capability">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveGrantClick(grant)}
                              color="error"
                              aria-label="Remove capability from bundle"
                            >
                              <RemoveCircleOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Delete Bundle Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteClose}>
        <DialogTitle>Delete Bundle</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the bundle "{bundle.name}"? This action cannot be undone
            and will remove all capability grants and assignments associated with this bundle.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Grant Dialog */}
      <Dialog open={addGrantDialogOpen} onClose={handleAddGrantClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add Capability to Bundle</DialogTitle>
        <DialogContent>
          <div className="space-y-4 pt-2">
            <Autocomplete
              options={availableCapabilities}
              getOptionLabel={option => option.name}
              value={newGrant.capability}
              onChange={(_, value) => setNewGrant(prev => ({ ...prev, capability: value }))}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Capability"
                  required
                  helperText="Select a capability to grant"
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <div>
                    <div className="font-medium">{option.name}</div>
                    {option.description && (
                      <div className="text-sm text-gray-500">{option.description}</div>
                    )}
                  </div>
                </li>
              )}
            />

            <Divider />

            <Typography variant="subtitle2" className="text-gray-600">
              Metering Configuration (Optional)
            </Typography>

            <FormControl fullWidth size="small">
              <InputLabel>Metering Type</InputLabel>
              <Select
                value={newGrant.metering_type}
                label="Metering Type"
                onChange={e => setNewGrant(prev => ({ ...prev, metering_type: e.target.value }))}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="fully_metered">Fully Metered</MenuItem>
                <MenuItem value="hybrid">Hybrid</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Quota"
              type="number"
              fullWidth
              size="small"
              value={newGrant.quota}
              onChange={e => {
                const val = e.target.value;
                if (val === '' || parseInt(val, 10) >= 1) {
                  setNewGrant(prev => ({ ...prev, quota: val }));
                }
              }}
              helperText="Usage limit (leave empty for unlimited)"
              inputProps={{ min: 1 }}
              InputProps={{
                endAdornment: newGrant.quota ? (
                  <Button
                    size="small"
                    onClick={() => setNewGrant(prev => ({ ...prev, quota: '' }))}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    Clear
                  </Button>
                ) : null,
              }}
            />

            <FormControl fullWidth size="small">
              <InputLabel>Usage Period</InputLabel>
              <Select
                value={newGrant.usage_period}
                label="Usage Period"
                onChange={e =>
                  setNewGrant(prev => ({ ...prev, usage_period: e.target.value }))
                }
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </Select>
            </FormControl>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddGrantClose}>Cancel</Button>
          <Button
            onClick={handleAddGrantSubmit}
            variant="contained"
            disabled={!newGrant.capability || isAddingGrant}
          >
            {isAddingGrant ? 'Adding...' : 'Add Capability'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Grant Dialog */}
      <Dialog open={removeGrantDialogOpen} onClose={handleRemoveGrantClose}>
        <DialogTitle>Remove Capability</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove the capability "
            {selectedGrant?.capability_name || selectedGrant?.capability_id}" from this bundle?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRemoveGrantClose}>Cancel</Button>
          <Button onClick={handleRemoveGrantConfirm} color="error" disabled={isRemovingGrant}>
            {isRemovingGrant ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Grant Dialog */}
      <Dialog open={editGrantDialogOpen} onClose={handleEditGrantClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Grant: {selectedGrant?.capability_name || selectedGrant?.capability_id}
        </DialogTitle>
        <DialogContent>
          <div className="space-y-4 pt-2">
            <FormControl fullWidth size="small">
              <InputLabel>Metering Type</InputLabel>
              <Select
                value={editGrantForm.metering_type}
                label="Metering Type"
                onChange={e =>
                  setEditGrantForm(prev => ({ ...prev, metering_type: e.target.value }))
                }
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="fully_metered">Fully Metered</MenuItem>
                <MenuItem value="hybrid">Hybrid</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Quota"
              type="number"
              fullWidth
              size="small"
              value={editGrantForm.quota}
              onChange={e => {
                const val = e.target.value;
                if (val === '' || parseInt(val, 10) >= 1) {
                  setEditGrantForm(prev => ({ ...prev, quota: val }));
                }
              }}
              helperText="Usage limit (leave empty for unlimited)"
              inputProps={{ min: 1 }}
              InputProps={{
                endAdornment: editGrantForm.quota ? (
                  <Button
                    size="small"
                    onClick={() => setEditGrantForm(prev => ({ ...prev, quota: '' }))}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    Clear
                  </Button>
                ) : null,
              }}
            />

            <FormControl fullWidth size="small">
              <InputLabel>Usage Period</InputLabel>
              <Select
                value={editGrantForm.usage_period}
                label="Usage Period"
                onChange={e =>
                  setEditGrantForm(prev => ({ ...prev, usage_period: e.target.value }))
                }
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </Select>
            </FormControl>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditGrantClose}>Cancel</Button>
          <Button
            onClick={handleEditGrantSubmit}
            variant="contained"
            disabled={isUpdatingGrant}
          >
            {isUpdatingGrant ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
